import React from 'react';
import type { HotspotCampoDTO, HotspotDTO } from '@webip/shared/src/dto/hotspot';

type Props = {
    hotspot: Partial<HotspotDTO>;
};

// Espelha `inputDoCampo` do backend (services/hotspot-campos.ts)
function inputDoCampo(c: Pick<HotspotCampoDTO, 'tipo' | 'formato' | 'papel'>): { type: string; placeholder: string } {
    switch (c.tipo) {
        case 'numero': return { type: 'number', placeholder: '0' };
        case 'data': return { type: 'date', placeholder: '' };
        case 'senha': return { type: 'password', placeholder: '••••••••' };
        default:
            switch (c.formato) {
                case 'cpf': return { type: 'text', placeholder: '000.000.000-00' };
                case 'telefone': return { type: 'tel', placeholder: '(00) 00000-0000' };
                case 'email': return { type: 'email', placeholder: 'email@exemplo.com' };
                default: return { type: 'text', placeholder: c.papel === 'usuario' ? 'usuário' : '' };
            }
    }
}

const fieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1.5px solid var(--border-1)',
    borderRadius: '8px',
    fontSize: '0.88rem',
    background: 'var(--surface-input)',
    boxSizing: 'border-box',
};

const HotspotPreview = ({ hotspot }: Props) => {
    const corPrimaria = hotspot.cor_primaria || '#3b82f6';
    const corSecundaria = hotspot.cor_secundaria || '#2563eb';
    const corFundo = hotspot.cor_fundo || '#f1f5f9';
    const corTexto = hotspot.cor_texto || '#1e293b';

    // Mesma regra do portal (HotspotPortalDTO.modo): com campo de papel "usuario"
    // o login pede só usuário (+ senha se obrigatória) e os termos ficam no
    // cadastro; sem ele, o login é o formulário completo.
    const campos = hotspot.campos ?? [];
    const modoCadastro = !campos.some((c) => c.papel === 'usuario');
    const camposLogin = modoCadastro
        ? campos
        : campos.filter((c) => c.papel === 'usuario' || (c.tipo === 'senha' && c.obrigatorio));

    return (
        <div style={{
            minHeight: '340px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: corFundo,
            ...(hotspot.fundo_url || hotspot.fundo_base64 ? {
                backgroundImage: `url(${hotspot.fundo_url ?? hotspot.fundo_base64})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            } : {}),
            borderRadius: '10px',
            padding: '16px',
        }}>
            <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                padding: '32px 28px',
                width: '100%',
                maxWidth: '360px',
                color: corTexto,
                fontFamily: 'system-ui, sans-serif',
            }}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    {hotspot.logo_url && (
                        <img
                            src={hotspot.logo_url}
                            alt="Logo"
                            style={{ maxHeight: '60px', maxWidth: '160px', marginBottom: '12px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
                        />
                    )}
                    <div style={{ fontSize: '1.2rem', fontWeight: 600, color: corPrimaria }}>
                        Acesso à Internet
                    </div>
                    {hotspot.descricao && (
                        <div style={{ fontSize: '0.82rem', color: corTexto, opacity: 0.7, marginTop: '4px' }}>
                            {hotspot.descricao}
                        </div>
                    )}
                </div>

                {camposLogin.map((campo) => (
                    <div key={campo.id ?? campo.nome} style={{ marginBottom: '12px' }}>
                        {campo.tipo === 'booleano' ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: 500, color: corTexto }}>
                                <input type="checkbox" disabled style={{ width: 'auto' }} />
                                {campo.nome}
                                {campo.obrigatorio && <span style={{ color: '#e53935' }}> *</span>}
                            </label>
                        ) : (
                            <>
                                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 500, marginBottom: '4px', color: corTexto }}>
                                    {campo.nome}
                                    {campo.obrigatorio && <span style={{ color: '#e53935' }}> *</span>}
                                </label>
                                {campo.tipo === 'lista' && campo.formato === 'opcoes' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {(campo.opcoes ?? []).map((op) => (
                                            <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: corTexto }}>
                                                <input type="radio" name={`preview-${campo.id ?? campo.nome}`} disabled style={{ width: 'auto' }} />
                                                {op}
                                            </label>
                                        ))}
                                        {!(campo.opcoes ?? []).length && (
                                            <span style={{ fontSize: '0.78rem', color: corTexto, opacity: 0.6 }}>Sem opções cadastradas</span>
                                        )}
                                    </div>
                                ) : campo.tipo === 'lista' ? (
                                    <select disabled style={{ ...fieldStyle, color: corTexto }}>
                                        <option>Selecione…</option>
                                        {(campo.opcoes ?? []).map((op) => (
                                            <option key={op}>{op}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type={inputDoCampo(campo).type}
                                        placeholder={inputDoCampo(campo).placeholder}
                                        disabled
                                        style={{ ...fieldStyle, color: corTexto }}
                                    />
                                )}
                            </>
                        )}
                    </div>
                ))}

                {modoCadastro && hotspot.termos_url && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: corTexto, marginBottom: '12px' }}>
                        <input type="checkbox" disabled style={{ width: 'auto' }} />
                        Li e aceito os{' '}
                        <span style={{ color: corPrimaria, textDecoration: 'underline' }}>termos de uso</span>
                        <span style={{ color: '#e53935' }}>*</span>
                    </label>
                )}

                <button
                    disabled
                    style={{
                        width: '100%',
                        padding: '10px',
                        background: corPrimaria,
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        marginTop: '6px',
                        cursor: 'default',
                        opacity: 0.9,
                    }}
                >
                    Conectar
                </button>

                <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '0.72rem', color: corSecundaria, opacity: 0.6 }}>
                    Ao continuar, você concorda com os termos de uso.
                </div>
            </div>
        </div>
    );
};

export default HotspotPreview;
